import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";

import { message } from "antd";

/** Also read by `components/tour/chapters/charts.ts`. */
export const COPY_TABLE_SUCCESS_CLASS = "copy-table-success";

interface UseChartDataTableCopyOptions {
  rows: Record<string, unknown>[];
  columns: Array<{ name: string; key: string }>;
}

export function useChartDataTableCopy({ rows, columns }: UseChartDataTableCopyOptions) {
  const [showCopySuccessMessage, setShowCopySuccessMessage] = useState<
    Record<string, boolean>
  >({});

  const copyCellContents = useCallback((cellValue: string | number) => {
    const textToCopy = cellValue?.toString() ?? "";
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success("Cell copied to clipboard!");
      })
      .catch((err) => {
        message.error("Failed to copy cell");
        // eslint-disable-next-line no-console
        console.error(`Failed to copy cell. ${err}`);
        Sentry.captureException(err, {
          extra: {
            operation: "copyCell",
            cellValue:
              cellValue !== null && cellValue !== undefined
                ? String(cellValue).substring(0, 100)
                : null
          }
        });
      });
  }, []);

  const copyRowContents = useCallback((row: Record<string, unknown>) => {
    const rowText = Object.values(row).join("\t");
    navigator.clipboard
      .writeText(rowText)
      .then(() => {
        message.success("Row copied to clipboard!");
      })
      .catch((err) => {
        message.error("Failed to copy row");
        // eslint-disable-next-line no-console
        console.error(`Failed to copy row. ${err}`);
        Sentry.captureException(err, {
          extra: {
            operation: "copyRow",
            rowKeys: Object.keys(row)
          }
        });
      });
  }, []);

  const copyColumnContents = useCallback((columnName: string, columnKey: string) => {
    setShowCopySuccessMessage((prev) => ({ ...prev, [columnKey]: true }));
    setTimeout(() => {
      setShowCopySuccessMessage((prev) => ({ ...prev, [columnKey]: false }));
    }, 1500);
  }, []);

  const copyColumnData = useCallback(
    (columnName: string, columnKey: string) => {
      const columnData = rows.map((row) => row[columnKey]);
      const headerAndData = [columnName, ...columnData].join("\n");
      navigator.clipboard
        .writeText(headerAndData)
        .then(() => {
          copyColumnContents(columnName, columnKey);
        })
        .catch((err) => {
          message.error("Failed to copy column");
          // eslint-disable-next-line no-console
          console.error(`Failed to copy column. ${err}`);
          Sentry.captureException(err, {
            extra: {
              operation: "copyColumn",
              columnName,
              columnKey,
              rowCount: rows.length
            }
          });
        });
    },
    [rows, copyColumnContents]
  );

  const copyTableData = useCallback(() => {
    // Create header row
    const headers = columns.map((col) => col.name).join("\t");

    // Create data rows
    const dataRows = rows
      .map((row) => {
        return columns.map((col) => row[col.key] ?? "").join("\t");
      })
      .join("\n");

    // Combine headers and data
    const tableText = `${headers}\n${dataRows}`;

    navigator.clipboard
      .writeText(tableText)
      .then(() => {
        // Classed so the guided walkthrough can watch for *this* copy.
        // The bare `.ant-message-notice` is shared with the cell and row
        // copies above, which a whole-table step must not advance on.
        message.success({
          content: "Table copied to clipboard!",
          className: COPY_TABLE_SUCCESS_CLASS
        });
      })
      .catch((err) => {
        message.error("Failed to copy table");
        // eslint-disable-next-line no-console
        console.error(`Failed to copy table. ${err}`);
        Sentry.captureException(err, {
          extra: {
            operation: "copyTable",
            rowCount: rows.length,
            columnCount: columns.length
          }
        });
      });
  }, [columns, rows]);

  return {
    copyCellContents,
    copyRowContents,
    copyColumnContents,
    copyColumnData,
    copyTableData,
    showCopySuccessMessage
  };
}
